import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { listarClientes } from '../../services/clientes';
import { criarProcesso, editarProcesso } from '../../services/processos';
import { tiposDisponiveis, STATUS_PROCESSO } from './catalogoUtils';
import styles from './ProcessoFormModal.module.css';
import useEsc from '../../hooks/useEsc';

/* status 'concluido' só é atingido via fluxo de conclusão de etapa
   (ProcessoDetalhe) -- aqui na edição livre só os outros três fazem sentido */
const STATUS_EDITAVEL = STATUS_PROCESSO.filter(s => s.valor !== 'concluido');

const norm = s => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
const rotuloCliente = c => c ? `${c.codigo ? c.codigo + ' - ' : ''}${c.nome}` : '';

function ClientePicker({ value, onChange }) {
  const [texto, setTexto]     = useState(value ? rotuloCliente(value) : '');
  const [aberto, setAberto]   = useState(false);
  const [clientes, setClientes] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    function fora(e) { if (ref.current && !ref.current.contains(e.target)) setAberto(false); }
    document.addEventListener('mousedown', fora);
    return () => document.removeEventListener('mousedown', fora);
  }, []);

  function abrir() {
    setAberto(true);
    listarClientes().then(setClientes).catch(() => {});
  }

  const filtrados = useMemo(() => {
    const q = norm(texto);
    if (!q) return clientes.slice(0, 40);
    const porCodigo = clientes.filter(c => norm(c.codigo).startsWith(q));
    const porNome   = clientes.filter(c => !porCodigo.includes(c) && norm(c.nome).includes(q));
    return [...porCodigo, ...porNome].slice(0, 40);
  }, [texto, clientes]);

  return (
    <div className={styles.picker} ref={ref}>
      <input
        className={styles.input}
        value={texto}
        placeholder="Buscar cliente por código ou nome..."
        onChange={e => { setTexto(e.target.value); setAberto(true); if (value) onChange(null); }}
        onFocus={abrir}
      />
      {aberto && (
        <div className={styles.pickerLista}>
          {filtrados.length ? filtrados.map(c => (
            <button
              type="button"
              key={c.id}
              onClick={() => { onChange(c); setTexto(rotuloCliente(c)); setAberto(false); }}
            >
              <span className={styles.pickerCod}>{c.codigo}</span>{c.nome}
            </button>
          )) : <div className={styles.pickerVazio}>Nenhum cliente encontrado</div>}
        </div>
      )}
    </div>
  );
}

/* processo presente = modo edição (PUT), ausente = criação (POST) */
function ProcessoFormModal({ onFechar, onCriado, onSalvo, processo, catalogo }) {
  useEsc(onFechar);
  const editando = !!processo;

  const [cliente, setCliente]       = useState(() => (
    processo ? { id: processo.cliente_id, codigo: processo.cliente_codigo, nome: processo.cliente_nome } : null
  ));
  const [tipo, setTipo]             = useState(processo?.tipo || '');
  const [responsavel, setResponsavel] = useState(processo?.responsavel || '');
  const [status, setStatus]         = useState(processo?.status === 'concluido' ? 'em_andamento' : (processo?.status || 'em_andamento'));
  const [salvando, setSalvando]     = useState(false);
  const [erro, setErro]             = useState('');

  const tipos = tiposDisponiveis(catalogo);

  async function salvar() {
    if (!cliente)  return setErro('Selecione um cliente.');
    if (!tipo)     return setErro('Selecione o tipo do processo.');
    setSalvando(true); setErro('');
    try {
      if (editando) {
        const atualizado = await editarProcesso(processo.id, {
          cliente_id: cliente.id, tipo, responsavel: responsavel.trim(), status,
        });
        onSalvo(atualizado);
      } else {
        const novo = await criarProcesso({ cliente_id: cliente.id, tipo, responsavel: responsavel.trim() });
        onCriado(novo);
      }
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitulo}>{editando ? 'Editar processo' : 'Novo processo'}</h2>
          <button className={styles.btnFechar} onClick={onFechar} type="button">
            <FiX size={18} />
          </button>
        </div>

        <div className={styles.form}>
          {erro && <p className={styles.erro}>{erro}</p>}

          <div className={styles.campo}>
            <span className={styles.label}>Cliente</span>
            <ClientePicker value={cliente} onChange={setCliente} />
          </div>

          <div className={styles.campo}>
            <span className={styles.label}>Tipo</span>
            <select className={styles.input} value={tipo} onChange={e => setTipo(e.target.value)}>
              <option value="">Selecione...</option>
              {tipos.map(t => <option key={t.chave} value={t.chave}>{t.nome}</option>)}
            </select>
          </div>

          <div className={styles.campo}>
            <span className={styles.label}>Responsável</span>
            <input
              className={styles.input}
              value={responsavel}
              onChange={e => setResponsavel(e.target.value)}
              placeholder="Nome de quem vai tocar o processo"
            />
          </div>

          {editando && (
            <div className={styles.campo}>
              <span className={styles.label}>Status</span>
              <select className={styles.input} value={status} onChange={e => setStatus(e.target.value)}>
                {STATUS_EDITAVEL.map(s => <option key={s.valor} value={s.valor}>{s.label}</option>)}
              </select>
            </div>
          )}

          <div className={styles.modalFooter}>
            <button className={styles.btnCancelar} onClick={onFechar} type="button">Cancelar</button>
            <button className={styles.btnSalvar} onClick={salvar} disabled={salvando} type="button">
              {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Criar processo'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProcessoFormModal;
